/**
 * Duration class for handling time periods with automatic conversion
 * This is a standalone utility with no backend dependencies, safe for frontend use
 */
export class Duration {
  private readonly _seconds: number;

  private constructor(seconds: number) {
    this._seconds = Math.max(0, seconds);
  }

  // Static factory methods
  static seconds(value: number): Duration {
    return new Duration(value);
  }

  static minutes(value: number): Duration {
    return new Duration(value * 60);
  }

  static hours(value: number): Duration {
    return new Duration(value * 60 * 60);
  }

  static days(value: number): Duration {
    return new Duration(value * 60 * 60 * 24);
  }

  static weeks(value: number): Duration {
    return new Duration(value * 60 * 60 * 24 * 7);
  }

  // Getters for different units
  get seconds(): number {
    return this._seconds;
  }

  get milliseconds(): number {
    return this._seconds * 1000;
  }

  get minutes(): number {
    return this._seconds / 60;
  }

  get hours(): number {
    return this._seconds / (60 * 60);
  }

  get days(): number {
    return this._seconds / (60 * 60 * 24);
  }

  get weeks(): number {
    return this._seconds / (60 * 60 * 24 * 7);
  }

  // Arithmetic operations
  add(other: Duration): Duration {
    return new Duration(this._seconds + other._seconds);
  }

  subtract(other: Duration): Duration {
    return new Duration(this._seconds - other._seconds);
  }

  multiply(factor: number): Duration {
    return new Duration(this._seconds * factor);
  }

  // Comparison
  equals(other: Duration): boolean {
    return this._seconds === other._seconds;
  }

  greaterThan(other: Duration): boolean {
    return this._seconds > other._seconds;
  }

  lessThan(other: Duration): boolean {
    return this._seconds < other._seconds;
  }

  // String representation
  toString(): string {
    const days = Math.floor(this.days);
    const hours = Math.floor(this.hours % 24);
    const minutes = Math.floor(this.minutes % 60);
    const seconds = Math.floor(this.seconds % 60);
    
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
    
    return parts.join(' ');
  }
}

// Convenience duration constants
export const duration = {
  minute: Duration.minutes(1),
  hour: Duration.hours(1),
  day: Duration.days(1),
  week: Duration.weeks(1),
  month: Duration.days(30), // Approximation
};

// ---------------------------------------------------------------------------
// Cache-Control helpers
// ---------------------------------------------------------------------------

import { format } from '@tusbar/cache-control';

export { format };

/**
 * Cache-Control value that prevents all caching.
 * Use on private or highly dynamic responses.
 */
export const CacheControlNoCache: string = format({
  noCache: true,
  noStore: true,
  private: true,
  public: false,
  mustRevalidate: true,
});

// ---------------------------------------------------------------------------
// Legacy millisecond constants (deprecated - use Duration class instead)
// ---------------------------------------------------------------------------

export const TTLS = {
  ONE_WEEK: 7 * 24 * 60 * 60 * 1000,
  ONE_DAY: 24 * 60 * 60 * 1000,
  ONE_HOUR: 60 * 60 * 1000,
  ONE_MINUTE: 60 * 1000,
};